import {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  type ChangeEvent,
  Component
} from 'react';
import { action } from '@storybook/addon-actions';

import { menuHelpers, useEvent, debounce } from '@pega/cosmos-react-core';
import type { InputProps, ForwardProps, MenuItemProps } from '@pega/cosmos-react-core';
import type {
  ExpressionBuilderWrapperProps,
  ExpressionRefInterface,
  ExpressionItemProps,
  ExpressionBuilderProps,
  HandleValue,
  InputParams,
  AutoCompleteTriggerChar,
  CursorPosition
} from '@pega/cosmos-react-build';

import {
  items,
  getFilterRegex,
  suggestedRuleType,
  getSuggestedFunction,
  getScopeFromPosition,
  inLineErrorsMocks,
  suggestedFields
} from '../ExpressionBuilder/ExpressionBuilder.mocks';

import { mockSuggestionList } from './Expression.mocks';

const inputParamChange = 'Input param change';

export default function useMockExpression({
  inLineError = 'single',
  showExpressionBuilderErrorBanner,
  expShowErrors
}: {
  inLineError?: string;
  showExpressionBuilderErrorBanner?: boolean;
  expShowErrors?: boolean;
}) {
  type SuggestionItemProps = MenuItemProps & { key?: string };
  const [showModal, setShowModal] = useState(false);
  const [selectedId, setSelectedId] = useState<MenuItemProps['id']>();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionList, setSuggestionList] = useState<SuggestionItemProps[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [expBuilderInputValue, setExpBuilderInputValue] = useState('');
  const [expressionItems, setExpressionItems] = useState<typeof items>([]);
  const [expressionDetails, setExpressionDetails] = useState<
    ExpressionBuilderProps['list']['details']
  >({});
  const [isExpBuilderLoading, setIsExpBuilderLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const filterRegex = useMemo(() => getFilterRegex(searchValue), [searchValue]);
  const [errors, setErrors] = useState('');
  const handle = useRef<HandleValue>(null);
  const expressionRef = useRef<ExpressionRefInterface>(null);
  const [loading, setLoading] = useState(false);
  const [inLineErrors, setInLineErrors] = useState<ExpressionBuilderWrapperProps['inLineErrors']>();
  const [expBuilderErrorBanner, setExpBuilderErrorBanner] =
    useState<ExpressionBuilderWrapperProps['expBuilderErrorBanner']>();
  const [expressionDetailsValues, setExpressionDetailsValues] = useState<{
    [id: string]: {
      [field: string]: any;
    };
  }>({});

  const detailsFilter = (location: { field: string; value: string }[], filterValue: string) =>
    location.find(field => field.value.toLowerCase().includes(filterValue));

  const openExpBuilderModal = async () => {
    setShowModal(true);
  };

  async function mockApiCall() {
    return new Promise<void>(resolve => {
      setTimeout(() => {
        resolve();
      }, 1000);
    });
  }

  useEffect(() => {
    if (showModal) {
      setIsExpBuilderLoading(true);
      mockApiCall().then(() => {
        setExpressionItems(items);
        setIsExpBuilderLoading(false);
      });
    }
  }, [showModal, items]);

  useEffect(() => {
    if (inLineErrorsMocks?.length && inLineError && expShowErrors) {
      const inLineErrorList = inLineErrorsMocks.slice(
        0,
        inLineError === 'single' ? 1 : inLineErrorsMocks.length
      );
      setInLineErrors(inLineErrorList);
      const messages: string[] = [];
      inLineErrorList.forEach(item => messages.push(...item.messages));
      setErrors(messages.join(', '));
    } else {
      setInLineErrors([]);
      setErrors('');
    }
  }, [inLineError, expShowErrors]);

  useEffect(() => {
    if (inLineErrorsMocks?.length && showExpressionBuilderErrorBanner) {
      const messages: string[] = [];
      inLineErrorsMocks.forEach(item => messages.push(...item.messages));
      setExpBuilderErrorBanner({
        variant: 'urgent',
        messages,
        onDismiss: () => {
          setExpBuilderErrorBanner(undefined);
        }
      });
    } else {
      setExpBuilderErrorBanner(undefined);
    }
  }, [showExpressionBuilderErrorBanner]);

  useEffect(() => {
    const lowerSearchFilter = searchFilter.toLowerCase();
    const lowerSearchValue = searchValue.toLowerCase();
    const filteredItems = items.filter(item => {
      return (
        item.type.toLowerCase().includes(lowerSearchFilter === 'all' ? '' : lowerSearchFilter) &&
        (item.primary.toLowerCase().includes(lowerSearchValue) ||
          detailsFilter(item.details.primary, lowerSearchValue) ||
          detailsFilter(item.details.output, lowerSearchValue))
      );
    });

    const matchedItems = filteredItems.map(item => {
      if (item.primary.toLowerCase().includes(lowerSearchValue)) {
        return item;
      }

      let matchedField;

      const primaryField = detailsFilter(item.details.primary, lowerSearchValue);
      if (primaryField) {
        matchedField = primaryField;
      }

      const outputField = detailsFilter(item.details.output, lowerSearchValue);
      if (outputField && !primaryField) {
        matchedField = outputField;
      }

      return {
        ...item,
        matchedField: { name: matchedField?.field, value: matchedField?.value }
      };
    });

    setExpressionItems(matchedItems);
  }, [searchValue, searchFilter]);

  useEffect(() => {
    if (showModal) {
      setExpBuilderInputValue(handle.current?.getExpression() ?? '');
    }
  }, [showModal]);

  const onInputParamChange = (id: string, field: string, value: any) => {
    action(inputParamChange)(id, field, value);
  };

  const expressionSubmitHandler = useCallback(
    ({ close }: { close: () => void }) => {
      if (!errors && !expBuilderErrorBanner) {
        setShowModal(false);
        setInputValue(expBuilderInputValue);
        close();
      }
    },
    [errors, expBuilderErrorBanner, expBuilderInputValue]
  );

  const expressionCancelHandler = ({ close }: { close?: () => void }) => {
    setShowModal(false);
    close?.();
  };

  const expBuilderFilterChangeHandler = (value: string) => {
    setSearchFilter(value);
  };

  const expBuilderSearchChangeHandler = (value: string) => {
    setSearchValue(value);
  };

  const onItemAdd = (
    id: ExpressionItemProps['id'],
    addExpression: (expression: string) => void
  ) => {
    const item = items.find(instance => instance.id === id);
    if (!item) {
      return;
    }
    if (item.type.toLowerCase() === 'field') {
      addExpression(item.expression);
    } else {
      addExpression(
        `${item.expression.slice(0, item.expression.length - 1)}${
          expressionDetailsValues[item.id]
            ? Object.keys(expressionDetailsValues[item.id])
                .map(field => `${expressionDetailsValues[item.id][field]}`)
                .join(', ')
            : ''
        })`
      );
    }
  };

  const onItemExpand = (id: string) => {
    action('onItemExpand')(id);
    const itemDetails = expressionItems.find(item => item.id === id)?.details;

    const fieldValueMapper = ({ field, value }: { field: string; value: string }) => {
      return {
        id: `${id}-${field}`,
        name: field,
        value
      };
    };

    if (itemDetails) {
      const primary = itemDetails.primary.map(fieldValueMapper);

      const outputParams = itemDetails.output?.map(fieldValueMapper);

      const inputParams = itemDetails.input?.map(({ id: fieldId, field, type }) => {
        const inputParam: InputParams<InputProps & ForwardProps> = {
          id: `${id}-${fieldId}`,
          renderer: Component,
          rendererProps: {
            label: field,
            onChange: (e: ChangeEvent<HTMLInputElement>) =>
              onInputParamChange(id, fieldId, e.target.value),
            type: type as InputProps['type']
          }
        };
        return inputParam;
      });

      setExpressionDetails((prev: ExpressionBuilderProps['list']['details']) => {
        return {
          ...prev,
          [id]: {
            primary,
            outputParams,
            inputParams
          }
        };
      });
    }
  };

  const onItemCollapse = (id: string) => {
    action('onItemCollapse')(id);
    setTimeout(() => {
      setExpressionDetails((prev: ExpressionBuilderProps['list']['details']) => {
        const newExpressionDetails = { ...prev };
        delete newExpressionDetails[id];
        return newExpressionDetails;
      });
    }, 500);

    setExpressionDetailsValues(prev => {
      const newValues = { ...prev };
      delete newValues[id];
      return newValues;
    });
  };

  const expressionChangeHandler = useRef(
    debounce(expression => {
      if (expression.includes('undefined')) {
        setErrors('Correct the invalid expression, expression cannot contain "undefined" ');
      } else {
        setExpBuilderInputValue(expression);
        setErrors('');
      }
    }, 600)
  ).current;

  const fetchSuggestions: ExpressionBuilderProps['fetchSuggestions'] = (
    autoCompleteTrigger: AutoCompleteTriggerChar,
    cursorPosition: CursorPosition
  ) => {
    action('cursorPosition')(cursorPosition);
    if (autoCompleteTrigger === '@') {
      setLoading(true);
      return new Promise(resolve => {
        setTimeout(() => {
          setLoading(false);
          resolve(suggestedRuleType);
        }, 1000);
      });
    }
    if (autoCompleteTrigger === ':') {
      const scope = getScopeFromPosition(
        cursorPosition,
        autoCompleteTrigger,
        handle.current?.getExpression() ?? ''
      ).split('@')[0];
      const suggestedFunctions = getSuggestedFunction(scope);

      setLoading(true);
      return new Promise(resolve => {
        setTimeout(() => {
          setLoading(false);
          resolve(suggestedFunctions);
        }, 1000);
      });
    }
    setLoading(true);
    return new Promise(resolve => {
      setTimeout(() => {
        setLoading(false);
        resolve(suggestedFields);
      }, 1000);
    });
  };

  const suggestionItemClickHandler = (id: MenuItemProps['id']) => {
    let valueSelected = suggestionList?.find(item => item.id === id)?.key || '';
    valueSelected = !inputValue.endsWith('.')
      ? `${inputValue}.${valueSelected}`
      : `${inputValue}${valueSelected}`;
    setSelectedId(id);
    setInputValue(valueSelected);
    setShowSuggestions(false);
    setSuggestionList([]);
  };

  const suggestionItemsToRender = useMemo(() => {
    let newItems = suggestionList;
    newItems = menuHelpers.mapTree(newItems, item => ({
      ...item,
      selected: selectedId ? selectedId === item.id : undefined
    })) as MenuItemProps[];
    return newItems;
  }, [suggestionList, selectedId]);

  const inputKeydownHandler = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === '.' || e.key === 'ArrowDown') && !showSuggestions) {
      setSuggestionList([]);
      await mockApiCall().then(() => {
        setSuggestionList(mockSuggestionList);
        setShowSuggestions(true);
      });
    } else if (showSuggestions && e.key === 'Escape') {
      setSelectedId(undefined);
      setShowSuggestions(false);
    }
  };

  const inputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setSelectedId(undefined);
  };

  const handleClickOutside = (event: Event) => {
    if (
      expressionRef.current?.getPopOverRef() &&
      event.target instanceof Node &&
      !expressionRef.current.getPopOverRef()?.contains(event.target)
    ) {
      setShowSuggestions(false);
    }
  };

  useEvent('click', handleClickOutside);

  const resetExpression = () => {
    setInputValue('');
  };

  return {
    showExpBuilderModal: showModal,
    setShowExpBuilderModal: setShowModal,
    openExpBuilderModal,
    showSuggestions,
    suggestionList,
    inputValue,
    expBuilderInputValue,
    expressionItems,
    expressionDetails,
    isExpBuilderLoading,
    searchFilter,
    setSearchFilter,
    filterRegex,
    searchValue,
    setSearchValue,
    errors,
    handle,
    expressionRef,
    loading,
    inLineErrors,
    expBuilderErrorBanner,
    expressionSubmitHandler,
    expressionCancelHandler,
    onItemAdd,
    onItemExpand,
    onItemCollapse,
    expressionChangeHandler,
    fetchSuggestions,
    suggestionItemsToRender,
    inputKeydownHandler,
    inputChangeHandler,
    expBuilderFilterChangeHandler,
    expBuilderSearchChangeHandler,
    suggestionItemClickHandler,
    resetExpression
  };
}
